import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-420" />;
export function Careers() {
  return <>
      <Helmet data-id="element-421">
        <title data-id="element-422">Careers — Lazy Monk Labs</title>
        <meta name="description" content="We work with people who can lose everything without losing themselves. Building diagnostic systems for human patterns." data-id="element-423" />
        <meta name="robots" content="index, follow" data-id="element-424" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-425">
        {/* Navigation */}
        <header data-id="element-426">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-427">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-428">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-429">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-430" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-431">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-432">
                <Link to="/work-mirror" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-medium" data-id="element-433">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-434">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-435">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-436">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-437">
          {/* 1. OPENING STATEMENT */}
          <section className="relative py-32 md:py-40 px-6 overflow-hidden" data-id="element-438">
            <CoordinateGrid data-id="element-439" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-16" data-id="element-440">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif leading-[1.1] text-[#F5F5F5]" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-441">
                We work with people who can lose everything without losing
                themselves.
              </h1>
            </div>
          </section>

          {/* 2. WHAT WE ARE BUILDING */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-442">
            <div className="max-w-4xl mx-auto space-y-12" data-id="element-443">
              <h2 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-444">
                What we are building
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-445">
                <p data-id="element-446">
                  Lazy Monk Labs is building diagnostic systems for human
                  patterns.
                </p>
                <p data-id="element-447">
                  Products like Decoder and Conscious OS treat the human mind
                  like an operating system — something that can be diagnosed,
                  understood, and re-architected.
                </p>
                <p className="text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-448">
                  We are not building a company. We are building a
                  civilization-grade system for how humans understand themselves
                  and their patterns.
                </p>
              </div>
            </div>
          </section>

          {/* 3. THE MISSION */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-449">
            <div className="max-w-4xl mx-auto space-y-12" data-id="element-450">
              <h2 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-451">
                The mission
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-452">
                <p data-id="element-453">The world runs on broken mental operating systems.</p>
                <p data-id="element-454">
                  Most suffering, bad decisions, bad leadership, and bad
                  organizations come from undiagnosed inner systems.
                </p>
                <p className="text-[#F5F5F5]" data-id="element-455">
                  Lazy Monk Labs is building the diagnostic and engineering
                  layer for human consciousness.
                </p>
              </div>
            </div>
          </section>

          {/* 4. THE BUILDER'S RESILIENCE FILTER */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-456">
            <div className="max-w-4xl mx-auto space-y-16" data-id="element-457">
              <h2 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-458">
                What we look for
              </h2>

              <div className="space-y-12" data-id="element-459">
                {/* Capacity to Bet */}
                <div className="space-y-4" data-id="element-460">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-461">
                    Capacity to Bet
                  </h3>
                  <p className="text-lg text-gray-400 leading-relaxed" data-id="element-462">
                    You've chosen uncertainty over comfort at least once in your
                    life.
                  </p>
                </div>

                {/* Ego Containment */}
                <div className="space-y-4" data-id="element-463">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-464">
                    Ego Containment
                  </h3>
                  <p className="text-lg text-gray-400 leading-relaxed" data-id="element-465">
                    You can admit when you're wrong without protecting your
                    identity.
                  </p>
                </div>

                {/* Regenerative Confidence */}
                <div className="space-y-4" data-id="element-466">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-467">
                    Regenerative Confidence
                  </h3>
                  <p className="text-lg text-gray-400 leading-relaxed" data-id="element-468">
                    You trust your ability to rebuild more than any single
                    outcome.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. WHO SHOULD APPLY (AND WHO SHOULDN'T) */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-469">
            <div className="max-w-4xl mx-auto" data-id="element-470">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-471">
                {/* This is not for */}
                <div className="space-y-8" data-id="element-472">
                  <h2 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-473">
                    This is not for people who
                  </h2>
                  <div className="space-y-4 text-gray-500" data-id="element-474">
                    <p data-id="element-475">• Want safety-first careers</p>
                    <p data-id="element-476">• Need structure before responsibility</p>
                    <p data-id="element-477">• Want a brand name more than a mission</p>
                    <p data-id="element-478">• Want to be told what to do</p>
                  </div>
                </div>

                {/* This is for */}
                <div className="space-y-8" data-id="element-479">
                  <h2 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-480">
                    This is for people who
                  </h2>
                  <div className="space-y-4 text-gray-400" data-id="element-481">
                    <p data-id="element-482">• Want to build something that didn't exist before</p>
                    <p data-id="element-483">• Are comfortable with ambiguity and first principles</p>
                    <p data-id="element-484">
                      • Want their work to matter at a civilizational, not
                      cosmetic, level
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 6. WHAT WE ARE NOT */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-485">
            <div className="max-w-4xl mx-auto space-y-8" data-id="element-486">
              <h2 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-487">
                What we are not
              </h2>

              <div className="space-y-4 text-lg text-gray-500" data-id="element-488">
                <p data-id="element-489">• Not a self-help company</p>
                <p data-id="element-490">• Not a coaching business</p>
                <p data-id="element-491">• Not an astrology or spirituality brand</p>
                <p data-id="element-492">• Not a motivational content company</p>
              </div>

              <p className="text-lg text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-493">
                We are building diagnostic systems. Calm. Precise. Serious.
              </p>
            </div>
          </section>

          {/* 7. CALL TO ACTION */}
          <section className="py-32 px-6 relative overflow-hidden border-t border-[#2B2B2B]" data-id="element-494">
            <CoordinateGrid data-id="element-495" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-12" data-id="element-496">
              <div className="space-y-8" data-id="element-497">
                <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-498">
                  We are not running a hiring process.
                  <br data-id="element-499" />
                  We are assembling a very small group of builders.
                </p>

                <p className="text-lg text-[#F5F5F5] leading-relaxed pt-6 border-t border-[#2B2B2B]" data-id="element-500">
                  If you read this and felt a quiet "yes" — write to:{' '}
                  <a href="mailto:anish@lazymonklabs.com" className="underline hover:text-gray-400 transition-colors" data-id="element-501">
                    anish@lazymonklabs.com
                  </a>
                </p>
              </div>

              <div className="pt-8 space-y-6 text-base text-gray-500 leading-relaxed border-t border-[#2B2B2B]" data-id="element-502">
                <p className="text-[#F5F5F5]" data-id="element-503">Don't send a resume.</p>
                <p data-id="element-504">Send a note about:</p>
                <div className="space-y-3 pl-6" data-id="element-505">
                  <p data-id="element-506">• What you've built</p>
                  <p data-id="element-507">• What you've broken</p>
                  <p data-id="element-508">• What you've rebuilt</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-509">
          <div className="max-w-6xl mx-auto" data-id="element-510">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-511">
              <div data-id="element-512">
                <p className="text-sm text-gray-500 font-mono" data-id="element-513">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-514">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-515">
                <Link to="/work-mirror" className="hover:text-[#F5F5F5] transition-colors" data-id="element-516">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-517">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-518">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-519">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-520">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}